import type { EcommerceItem } from '../../../../../types/analytics/ECommerce';
import type { Fee, Tax, Variant } from '../../../../../types/CoreShoppingExperience';
import { PaymentOption, PurchaseMode } from '../../../../../types/CoreShoppingExperience';
import UpgradeType from '../../../../../types/airline/UpgradeType';
import isAutoCampaign from '../../campaign/isAutoCampaign';
import { hasContinuousPricingFromPurchaseOption } from './hasContinuousPricing';
import type { OptionalItems } from '../../../../../types/analytics/AnalyticsEvent';

const convertBidOptionToItem = (
  quantity: number,
  textkeyGroup: string,
  upgradeType: string,
  bidAmount: number,
  variant: Variant,
  pointsAmount?: number,
  variantPurchaseMode?: PurchaseMode,
  optionalItems?: OptionalItems,
  // eslint-disable-next-line max-params, sonarjs/cognitive-complexity
): EcommerceItem => {
  const cashPurchaseOption = variant.purchaseOptions.find((x) => x.paymentOption === PaymentOption.CASH);
  const pointsPurchaseOption = variant.purchaseOptions.find((x) => x.paymentOption === PaymentOption.POINTS);
  const cashItemName = cashPurchaseOption && bidAmount > 0 ? `- ${cashPurchaseOption?.paymentOption} ` : '';
  const pointsItemName =
    pointsPurchaseOption && pointsAmount && pointsAmount > 0 ? `- ${pointsPurchaseOption?.paymentOption}` : '';

  const purchaseMode = variantPurchaseMode ?? cashPurchaseOption?.purchaseMode ?? pointsPurchaseOption?.purchaseMode;
  const item: EcommerceItem = {
    index: undefined,
    itemName: `${upgradeType} - ${purchaseMode} ${cashItemName}${pointsItemName}`,
    itemBrand: textkeyGroup,
    price: bidAmount,
    pointsPrice: pointsAmount,
    tax:
      cashPurchaseOption && bidAmount > 0
        ? cashPurchaseOption?.paymentBreakdown.taxes?.reduce((acc: number, tax: Tax) => acc + tax.amount, 0)
        : 0,
    fee:
      cashPurchaseOption && bidAmount > 0
        ? cashPurchaseOption?.paymentBreakdown.fees?.reduce((acc: number, tax: Fee) => acc + tax.amount, 0)
        : 0,
    pointsTax:
      pointsPurchaseOption && pointsAmount && pointsAmount > 0
        ? pointsPurchaseOption?.paymentBreakdown.taxes?.reduce((acc: number, tax: Tax) => acc + tax.amount, 0)
        : 0,
    pointsFee:
      pointsPurchaseOption && pointsAmount && pointsAmount > 0
        ? pointsPurchaseOption?.paymentBreakdown.fees?.reduce((acc: number, tax: Fee) => acc + tax.amount, 0)
        : 0,
    quantity,
    upgradeType,
    fromCabin: variant.fromCabin,
    purchaseMode,
    purchaseOptionSettings: undefined,
    hasSeatMapOption: upgradeType === UpgradeType.EXTRA_SEAT,
    seats: optionalItems?.seats,
    blockedSeats: optionalItems?.blockedSeats,
    discount: cashPurchaseOption?.campaign?.percentReduction ?? 0,
    autoCampaign: isAutoCampaign(cashPurchaseOption?.campaign),
    paymentOptions: variant.paymentOptions,
    pointsPaymentOption: pointsPurchaseOption?.paymentOption,
    autoCampaignDiscount:
      cashPurchaseOption?.campaign && isAutoCampaign(cashPurchaseOption?.campaign)
        ? cashPurchaseOption?.campaign.percentReduction
        : 0,
    continuousPricing: hasContinuousPricingFromPurchaseOption(cashPurchaseOption),
    ...(optionalItems?.isFallback && { isFallBack: optionalItems?.isFallback }),
  };

  if (cashPurchaseOption?.purchaseMode === PurchaseMode.BID) {
    item.purchaseOptionSettings = cashPurchaseOption.settings;
  }
  if (pointsPurchaseOption?.purchaseMode === PurchaseMode.BID) {
    item.purchaseOptionSettings = pointsPurchaseOption.settings;
  }

  return item;
};

export default convertBidOptionToItem;
